import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { Setting } from '../../types';
import { type SettingsData } from '../data/use-settings-data';
import { Block } from './block';
import { type ItemType, SettingItem } from './setting-item';

type SettingBlockProps<T extends Setting> = ItemType & {
  data: Record<T, SettingsData>;
  title: string;
};

export function SettingBlock<T extends Setting>(props: SettingBlockProps<T>) {
  const { data, title, type } = props;

  const settings = useMemo(
    () =>
      Object.entries(data).map(([settingName, settingData]) => ({
        key: settingName,
        settingName: settingName as Setting,
        ...settingData
      })),
    [data]
  );

  const itemTypeProps = useMemo<ItemType>(() => {
    if (type === 'radio') {
      const { radioSettingName } = props;

      return { type: 'radio', radioSettingName };
    }

    return { type: 'switch' };
  }, [type, props]);

  return (
    <Block withTitle>
      <Title role="heading" aria-level={2}>
        {title}
      </Title>

      {settings.map(({ key, ...itemProps }) => (
        <SettingItem key={key} {...itemProps} {...itemTypeProps} />
      ))}
    </Block>
  );
}

const Title = styled.div`
  ${({ theme }) => css`
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
    color: ${theme.settingBlock.titleColor};
    padding: 0 8px 0 12px;
  `}
`;
